import React from "react";
import ChartGoal from "charts/ChartGoal";
import ChartLine from "charts/ChartLine";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import KPIMetricBlock from "dashboards/KPIMetricBlock";
import MetricFormatter from "dashboards/MetricFormatter";
import KeywordBlock from "dashboards/KeywordBlock";
import KeywordImprovedDeclinedBlock from "dashboards/KeywordImprovedDeclinedBlock";
import KeywordTable from "dashboards/KeywordTable";
import MetricTablePdf from "MetricTablePdf";
import ServiceDisabled from "dashboards/ServiceDisabled";
import GetFormatter from "dashboards/GetFormatter";
import NoData from "dashboards/NoData";
import jsDig from "utils/jsDig";
import allMetrics from "data/allMetrics.js";
import PDFPage from "pdfs/PDFPage";
import { camelCase, cloneDeep } from "lodash-es";
import getNegative from "../utils/getNegative";
import getGoalLocked from "utils/getGoalLocked";
import dashboardTableOrder from "../utils/dashboardTableOrder";

const allMetricData = allMetrics();

class SEOPdf extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};

    this.goalChartDim = "135x135";
    this.lineChartDim = "350x250";
    this.pieChartDim = "175x175";

    this.goalsPerRow = 5;
    this.chartsPerRow = 2;
    this.tablesPerRow = 1;
    this.kpiBlocksPerRow = 3;
    this.metricsPerTable = 4;

    this.goalRowHeight = 144;
    this.chartRowHeight = 300;
    this.tableRowHeight = 315;
    this.groupTableRowHeight = 355;
    this.kpiBlockRowHeight = 200;
    this.keywordRankingRowHeight = 25;
  }

  generatePdfSection(section, cc_data, dashboard) {
    const metricData = allMetricData["metricData"];
    let date = this.props.date ? moment(this.props.date) : moment();
    let month = date.format("MMM");
    let year = date.year();
    let landingPageCase;
    let keywordCase;
    // console.log(section, "looking for keyword");
    function categorize(string) {
      if (string.includes("landingPageGroups")) {
        landingPageCase = string;
      }
      if (string.includes("keywordGroups")) {
        keywordCase = string;
      }
      return string;
    }
    if (section.metrics_data) {
      const renderArray = dashboardTableOrder("seo", Object.keys(section.metrics_data));
      switch (categorize(section.name)) {
        case "goalsAndProjections":
          let goals = this.props.goals;
          return (
            <>
              <h2 style={{ textAlign: "center" }}>Goals & Projections </h2>
              <Row xs={this.goalsPerRow} className="justify-content-center">
                {renderArray.map((metricName, index) => {
                  const metricKey = jsDig([metricName, "name", "snakeCase"], metricData);
                  const metricShortened = jsDig([metricName, "name", "shortened"], metricData);
                  const current = jsDig([dashboard, metricKey, "raw"], cc_data);
                  const goal = parseFloat(jsDig([dashboard, year, month, camelCase(metricKey)], goals));
                  const projected = jsDig([dashboard, metricKey, "projected"], cc_data);
                  const negative = getNegative(section.name, metricKey, projected - goal, cc_data);
                  const goalLocked = getGoalLocked(date, metricKey);
                  if (metricKey) {
                    if (jsDig([dashboard, metricKey, "raw"], cc_data) != null) {
                      if (section.metrics_data[metricName] == "1") {
                        let Formatter = GetFormatter({ metricName: metricKey });
                        return (
                          <Col key={index}>
                            <ChartGoal
                              metricName={metricShortened}
                              current={current}
                              goal={goal}
                              projected={projected}
                              formatter={Formatter}
                              negative={negative}
                              goalLocked={goalLocked}
                              baseCurrency={this.props.base_currency}
                              displayCurrency={this.props.display_currency}
                              pdfDim={this.goalChartDim}
                            />
                          </Col>
                        );
                      }
                    } else {
                      return (
                        <p className="text-danger" key={index}>
                          {metricKey} was not found in cc_data
                        </p>
                      );
                    }
                  } else {
                    return (
                      <p className="text-danger" key={index}>
                        {metricName} not defined in metricData.json
                      </p>
                    );
                  }
                })}
              </Row>
            </>
          );
        case "primaryKpiCharts":
          return (
            <>
              <h2 style={{ textAlign: "center" }}>Primary KPI's</h2>

              <Row sm={this.chartsPerRow} className="justify-content-center">
                {renderArray.map((metricName, index) => {
                  const metricKey = jsDig([metricName, "name", "snakeCase"], metricData);
                  const metricShortened = jsDig([metricName, "name", "shortened"], metricData);
                  if (metricKey) {
                    if (jsDig([dashboard, "metrics_by_months", metricKey], cc_data) != null) {
                      if (section.metrics_data[metricName] == "1") {
                        let Formatter = GetFormatter({ metricName: metricKey, tick: true });
                        return (
                          <Col key={index}>
                            <ChartLine
                              metricName={metricShortened}
                              currentYear={jsDig([dashboard, "metrics_by_months", metricKey, 0, "year"], cc_data)}
                              currentYearData={jsDig([dashboard, "metrics_by_months", metricKey, 0, "data"], cc_data)}
                              previousYear={jsDig([dashboard, "metrics_by_months", metricKey, 1, "year"], cc_data)}
                              previousYearData={jsDig([dashboard, "metrics_by_months", metricKey, 1, "data"], cc_data)}
                              tickFormatter={Formatter}
                              baseCurrency={this.props.base_currency}
                              displayCurrency={this.props.display_currency}
                              pdfDim={this.lineChartDim}
                            />
                          </Col>
                        );
                      }
                    } else {
                      return (
                        <p className="text-danger" key={index}>
                          {metricKey} was not found in metrics_by_month in cc_data
                        </p>
                      );
                    }
                  } else {
                    return (
                      <p className="text-danger" key={index}>
                        {metricName} not defined in metricData.json
                      </p>
                    );
                  }
                })}
              </Row>
            </>
          );
        case "primaryKpiTables":
          let metricTitles = [];
          return (
            <>
              <h2>Primary Kpi Tables</h2>
              <Row sm={this.tablesPerRow} className="justify-content-center">
                {renderArray.map((metricName, index) => {
                  const metricKey = jsDig([metricName, "name", "snakeCase"], metricData);
                  if (metricKey) {
                    metricTitles.push(metricKey);
                    if (jsDig([dashboard, "metrics_2_years", metricKey], cc_data) == null) {
                      return (
                        <p className="text-danger" key={index}>
                          {metricKey} was not found in metrics_2_years in cc_data
                        </p>
                      );
                    }
                  } else {
                    return (
                      <p className="text-danger" key={index}>
                        {metricName} not defined in metricData.json
                      </p>
                    );
                  }
                })}

                {[...Array(Math.ceil(metricTitles.length / 4))].map((x, index) => {
                  return (
                    <Col className="skinny-table" key={index}>
                      <MetricTablePdf
                        table={jsDig([dashboard, "metrics_2_years"], cc_data)}
                        headerOrder={metricTitles.splice(0, 4)}
                        date={this.props.date}
                        scrollMessage={false}
                        responsive={false}
                        baseCurrency={this.props.base_currency}
                        displayCurrency={this.props.display_currency}
                      />
                    </Col>
                  );
                })}
              </Row>
            </>
          );
        case "allKpisVsPreviousYear":
          return (
            <>
              <h2 style={{ textAlign: "center" }}>KPI's vs Previous Year</h2>

              <div className="metric-blocks">
                {renderArray.map((metricName, index) => {
                  const metricKey = jsDig([metricName, "name", "snakeCase"], metricData);
                  const metricShortened = jsDig([metricName, "name", "shortened"], metricData);
                  if (metricKey) {
                    if (jsDig([dashboard, metricKey], cc_data) != null) {
                      if (section.metrics_data[metricName] == "1") {
                        let Formatter = GetFormatter({ metricName: metricKey });
                        const negative = getNegative(section.name, metricKey, jsDig([dashboard, metricKey, "projected_yy"], cc_data));
                        return (
                          <KPIMetricBlock
                            metricName={metricShortened}
                            metric={<Formatter value={jsDig([dashboard, metricKey, "projected"], cc_data)} baseCurrency={this.props.base_currency} displayCurrency={this.props.display_currency} />}
                            yy={<MetricFormatter.YY value={jsDig([dashboard, metricKey, "projected_yy"], cc_data)} />}
                            negative={negative}
                            key={index}
                          />
                        );
                      }
                    } else {
                      return (
                        <p className="text-danger d-inline-block" key={index}>
                          {metricKey} was not found in cc_data
                        </p>
                      );
                    }
                  } else {
                    return (
                      <p className="text-danger d-inline-block" key={index}>
                        {metricName} not defined in metricData.json
                      </p>
                    );
                  }
                })}
              </div>
            </>
          );
        case landingPageCase:
          const pageName = landingPageCase.replace("landingPageGroups", "");
          const landingPage = this.props.landing_page_groups.find((el) => el.name == pageName);
          return (
            <>
              {[landingPage.id].map((group_id, index) => {
                let metricTitles = [];
                return (
                  <Row sm={this.tablesPerRow} className="justify-content-center" key={index}>
                    <h2 className="text-center">{landingPage.name}</h2>
                    {renderArray.map((metricName, index) => {
                      const metricKey = jsDig([metricName, "name", "snakeCase"], metricData);
                      if (metricKey) {
                        metricTitles.push(metricKey);
                        if (jsDig([group_id, "metrics_2_years", metricKey], cc_data) == null) {
                          return (
                            <p className="text-danger" key={index}>
                              {metricKey} was not found in metrics_2_years in cc_data for landing page group {group_id}
                            </p>
                          );
                        }
                      } else {
                        return (
                          <p className="text-danger" key={index}>
                            {metricName} not defined in metricData.json
                          </p>
                        );
                      }
                    })}

                    {[...Array(Math.ceil(metricTitles.length / 4))].map((x, index) => {
                      return (
                        <Col className="skinny-table" key={index}>
                          <MetricTablePdf
                            table={jsDig([group_id, "metrics_2_years"], cc_data)}
                            headerOrder={metricTitles.splice(0, 4)}
                            date={this.props.date}
                            scrollMessage={false}
                            responsive={false}
                            baseCurrency={this.props.base_currency}
                            displayCurrency={this.props.display_currency}
                          />
                        </Col>
                      );
                    })}
                  </Row>
                );
              })}
            </>
          );
        case "backlinks":
          return (
            <>
              <h2>Backlinks</h2>
              <div className="metric-blocks">
                {renderArray.map((metricName, index) => {
                  const metricKey = jsDig([metricName, "name", "snakeCase"], metricData);
                  const metricShortened = jsDig([metricName, "name", "shortened"], metricData);
                  if (metricKey) {
                    if (jsDig([dashboard, metricKey], cc_data) != null) {
                      if (section.metrics_data[metricName] == "1") {
                        let Formatter = GetFormatter({ metricName: metricKey });
                        const negative = getNegative(section.name, metricKey, jsDig([dashboard, metricKey, "projected_yy"], cc_data));
                        return (
                          <KPIMetricBlock
                            metricName={metricShortened}
                            metric={<Formatter value={jsDig([dashboard, metricKey, "projected"], cc_data)} baseCurrency={this.props.base_currency} displayCurrency={this.props.display_currency} />}
                            yy={<MetricFormatter.YY value={jsDig([dashboard, metricKey, "projected_yy"], cc_data)} />}
                            negative={negative}
                            key={index}
                          />
                        );
                      }
                    } else {
                      return (
                        <p className="text-danger d-inline-block" key={index}>
                          {metricKey} was not found in cc_data
                        </p>
                      );
                    }
                  } else {
                    return (
                      <p className="text-danger d-inline-block" key={index}>
                        {metricName} not defined in metricData.json
                      </p>
                    );
                  }
                })}
              </div>
            </>
          );
        case keywordCase:
          const keywordName = keywordCase.replace("keywordGroups", "");
          const keywordGroup = this.props.keyword_groups.find((el) => el.name == keywordName);
          return (
            <>
              {[keywordGroup].map((group, index) => {
                console.log(group);
                console.log(section);
                const keywordGroup = group;
                const group_id = group.id;
                let metricTitle = this.props.keyword_groups.map((group) => group.name)[index];
                let metricTitles = [];
                metricTitles.push(metricTitle);
                return (
                  <div key={index}>
                    <Row sm={1} className="justify-content-center">
                      <h4 className="text-center mt-4">Keyword: {keywordGroup.name}</h4>

                      <Col className="skinny-table">
                        <MetricTablePdf
                          table={jsDig([0, "keyword_traffic", group_id], cc_data)}
                          headerOrder={["traffic"]}
                          date={this.props.date}
                          scrollMessage={false}
                          responsive={false}
                        />
                      </Col>
                    </Row>
                  </div>
                );
              })}
            </>
          );
        case "keywordSummary":
          return (
            <>
              <h2>Keyword Summary</h2>
              <Row xs={2}>
                {renderArray.map((metricName, index) => {
                  const metricKey = jsDig([metricName, "name", "snakeCase"], metricData);
                  const metricShortened = jsDig([metricName, "name", "shortened"], metricData);
                  if (metricName == "Improved") {
                    if (section.metrics_data[metricName] == "1") {
                      return (
                        <Col>
                          <KeywordImprovedDeclinedBlock
                            improved={jsDig([dashboard, "keywords_improved", "raw"], cc_data)}
                            declined={jsDig([dashboard, "keywords_declined", "raw"], cc_data)}
                          />
                        </Col>
                      );
                    }
                  } else if (metricKey) {
                    if (jsDig([dashboard, metricKey], cc_data) != null) {
                      if (section.metrics_data[metricName] == "1") {
                        return (
                          <Col>
                            <KeywordBlock
                              metricName={metricShortened}
                              metric={jsDig([dashboard, metricKey, "raw"], cc_data)}
                              yy={jsDig([dashboard, `${metricKey}_percentage`, "raw"], cc_data)}
                            />
                          </Col>
                        );
                      }
                    } else {
                      return (
                        <p className="text-danger d-inline-block" key={index}>
                          {metricKey} was not found in cc_data
                        </p>
                      );
                    }
                  } else {
                    return (
                      <p className="text-danger d-inline-block" key={index}>
                        {metricName} not defined in metricData.json
                      </p>
                    );
                  }
                })}
              </Row>
            </>
          );
        case "keywordRankings":
          let sortedKeywords = jsDig([dashboard, "keyword_rankings"], cc_data);
          switch (this.props.keyword_sort) {
            case "keyword":
              sortedKeywords.sort((a, b) => {
                if (a.keyword.toLowerCase() < b.keyword.toLowerCase()) return -1;
                if (a.keyword.toLowerCase() > b.keyword.toLowerCase()) return 1;
                return 0;
              });
              break;
            case "prevpos":
              sortedKeywords.sort((a, b) => {
                if (typeof a.start_date_ranking == "string") a.start_date_ranking = parseFloat(a.start_date_ranking);
                if (typeof b.start_date_ranking == "string") b.start_date_ranking = parseFloat(b.start_date_ranking);

                if (a.start_date_ranking == 0) return 1;
                if (b.start_date_ranking == 0) return -1;
                if (a.start_date_ranking < b.start_date_ranking) return -1;
                if (a.start_date_ranking > b.start_date_ranking) return 1;
                return 0;
              });
              break;
            case "currentpos":
              sortedKeywords.sort((a, b) => {
                if (a.end_date_ranking == 0) return 1;
                if (b.end_date_ranking == 0) return -1;
                if (a.end_date_ranking < b.end_date_ranking) return -1;
                if (a.end_date_ranking > b.end_date_ranking) return 1;
                return 0;
              });
              break;
            case "diff":
              sortedKeywords.sort((a, b) => {
                if (a.ranking_difference < b.ranking_difference) return -1;
                if (a.ranking_difference > b.ranking_difference) return 1;
                return 0;
              });
              break;
            case "cpc":
              sortedKeywords.sort((a, b) => {
                if (isNaN(parseFloat(a.cpc))) return -1;
                if (isNaN(parseFloat(b.cpc))) return 1;
                if (parseFloat(a.cpc) < parseFloat(b.cpc)) return -1;
                if (parseFloat(a.cpc) > parseFloat(b.cpc)) return 1;
                return 0;
              });
              break;
            case "volume":
              sortedKeywords.sort((a, b) => {
                if (isNaN(parseFloat(a.volume))) return -1;
                if (isNaN(parseFloat(b.volume))) return 1;
                if (parseFloat(a.volume) < parseFloat(b.volume)) return -1;
                if (parseFloat(a.volume) > parseFloat(b.volume)) return 1;
                return 0;
              });
              break;
            case "difficulty":
              sortedKeywords.sort((a, b) => {
                if (isNaN(parseFloat(a.difficulty))) return -1;
                if (isNaN(parseFloat(b.difficulty))) return 1;
                if (parseFloat(a.difficulty) < parseFloat(b.difficulty)) return -1;
                if (parseFloat(a.difficulty) > parseFloat(b.difficulty)) return 1;
                return 0;
              });
              break;
          }
          if (!this.props.keyword_sort_order_asc) {
            sortedKeywords.reverse();
          }
          return (
            <>
              <h2>Keyword Rankings</h2>
              <KeywordTable
                keywords={sortedKeywords.splice(0, renderArray.length)}
                metrics={jsDig(["dashboard_order", dashboard], this.props).find((el) => el.name == "keywordRankings")}
                keyword_sort_order_asc={this.props.keyword_sort_order_asc}
                keyword_sort={this.props.keyword_sort}
                pdf={true}
              />
            </>
          );
        default:
          return (
            <>
              <h4 className="text-danger">{section.name} is not a valid section name for this dashboard</h4>
            </>
          );
      }
    }
  }

  generatePdfPage(page, cc_data, dashboard, index) {
    if (page["metricNames"]) {
      let sectionNames = page["metricNames"].map((metric) => metric["section"]).filter((value, index, self) => self.indexOf(value) === index);
      let sections = [];
      // Create sections object and format how the dashboard generation loop expects
      sectionNames.forEach((section) => {
        sections.push({
          name: section,
          metrics_data: page["metricNames"]
            .filter((m) => m["section"] == section)
            .reduce((sum, item) => {
              sum[item["name"]] = "1";
              return sum;
            }, {}),
        });
      });

      console.log("Sections:");
      console.log(sections, sections.includes("landingPageGroups"));

      return (
        <PDFPage pageNumber={index + 1} hideLogo={this.props.white_label_service}>
          {index == 0 && (
            <div className="pdf-header justify-content-center">
              <h1 className="text-white text-center">{this.props.client_name} SEO Dashboard</h1>
              <h5 className="text-white text-center">{this.props.date_formatted}</h5>
            </div>
          )}
          {sections.map((section) => {
            return (
              <div
                className={`${
                  (section.name.includes("landingPageGroups") || section.name.includes("keywordGroups")) &&
                  Object.keys(section.metrics_data).length < 3 &&
                  "d-inline-block w-50 px-2"
                } `}>
                {this.generatePdfSection(section, cc_data, dashboard)}
              </div>
            );
          })}
        </PDFPage>
      );
    } else {
      return (
        <>
          <h4 className="text-danger">no metricsData in page</h4>
        </>
      );
    }
  }

  render() {
    const dashboard = this.props.dashboard;
    const cc_data = this.props.cc_data;
    const sectionList = jsDig(["metricsByDashboard", "seo", "sections"], allMetricData);
    const defaultMetrics = jsDig(["metricsByDashboard", "seo", "defaults", this.props.client_type], allMetricData);
    let dashboardOrder =
      jsDig([dashboard], this.props.dashboard_order) && jsDig([dashboard], this.props.dashboard_order).length > 0
        ? cloneDeep(jsDig([dashboard], this.props.dashboard_order))
        : Object.keys(sectionList).map((sectionName) => {
            let defaultMetricsData = defaultMetrics[sectionName] ? Object.keys(defaultMetrics[sectionName]) : [];
            return {
              name: sectionName,
              enabled: true,
              metrics_data: defaultMetricsData.reduce((sum, item) => {
                sum[item] = "1";
                return sum;
              }, {}),
            };
          });

    let landingPageIndex = dashboardOrder.findIndex((el) => el.name == "landingPageBreakdown");
    const copyOfLandingPageMetric = cloneDeep(dashboardOrder[landingPageIndex]);
    dashboardOrder.splice(landingPageIndex, 1);
    this.props.landing_page_groups.forEach((group) => {
      let duplicateLandingPage = copyOfLandingPageMetric;
      duplicateLandingPage["name"] = `landingPageGroups${group.name}`;
      dashboardOrder.splice(landingPageIndex, 0, cloneDeep(duplicateLandingPage));
      landingPageIndex += 1;
    });

    let keywordIndex = dashboardOrder.findIndex((el) => el.name == "keywordGroups");
    const copyOfKeywordMetric = cloneDeep(dashboardOrder[keywordIndex]);
    dashboardOrder.splice(keywordIndex, 1);
    this.props.keyword_groups.forEach((group) => {
      let duplicateKeywordGroup = copyOfKeywordMetric;
      duplicateKeywordGroup["name"] = `keywordGroups${group.name}`;
      dashboardOrder.splice(keywordIndex, 0, cloneDeep(duplicateKeywordGroup));
      keywordIndex += 1;
    });
    // PDFs can't be processed section this.props.keyword_sort section, we need to get every metric / chart that needs to display and process them sequentially to figure out how much can fit on each page
    let pages = [];
    let currentPage = 0;
    console.log(dashboardOrder, "entries");
    let firstLandingPageIndex = dashboardOrder.findIndex((el) => el.name.includes("landingPageGroups"));
    let firstKeywordIndex = dashboardOrder.findIndex((el) => el.name.includes("keywordGroups"));
    for (const [index, section] of dashboardOrder.entries()) {
      console.log(index, firstLandingPageIndex, section, "section", !((index - firstLandingPageIndex) % 2 == 0));
      let metricNames = section.metrics_data && Object.keys(section.metrics_data);
      if (section.name == "keywordGroups") {
        metricNames = this.props.keyword_groups.map((el) => el.name);
      }
      if (section.enabled) {
        if (section.metrics_data) {
          let elementsPerRow = 0;
          let elementHeight = 0;
          if (section.name == "goalsAndProjections") {
            elementsPerRow = this.goalsPerRow;
            elementHeight = this.goalRowHeight;
          } else if (section.name == "primaryKpiCharts") {
            elementsPerRow = this.chartsPerRow;
            elementHeight = this.chartRowHeight;
          } else if (section.name == "primaryKpiTables") {
            elementsPerRow = this.metricsPerTable;
            elementHeight = this.tableRowHeight;
          } else if (section.name == "allKpisVsPreviousYear") {
            elementsPerRow = this.kpiBlocksPerRow;
            elementHeight = this.kpiBlockRowHeight;
          } else if (section.name.includes("landingPageGroups") || section.name.includes("keywordGroups")) {
            elementsPerRow = this.metricsPerTable;
            if (
              ((dashboardOrder[index - 1] && dashboardOrder[index - 1].name.includes("landingPageGroups") && !((index - firstLandingPageIndex) % 2 == 0)) ||
                (dashboardOrder[index - 1] && dashboardOrder[index - 1].name.includes("keywordGroups") && !((index - firstKeywordIndex) % 2 == 0))) &&
              metricNames.length < 3
            ) {
              elementHeight = 0;
              console.log("group table height", 0);
            } else {
              console.log("group table height", this.groupTableRowHeight);
              elementHeight = this.groupTableRowHeight;
            }
          } else if (section.name == "backlinks") {
            elementsPerRow = this.kpiBlocksPerRow;
            elementHeight = this.kpiBlockRowHeight;
          } else if (section.name == "keywordSummary") {
            elementsPerRow = 2;
            elementHeight = this.kpiBlockRowHeight;
          } else if (section.name == "keywordRankings") {
            elementsPerRow = 1;
            elementHeight = this.keywordRankingRowHeight;
          } else {
            return section.name;
          }

          // Create pages array
          if (section.name == "keywordSummary") {
            metricNames = ["topThreeKeywords", "topTenKeywords", "topTwentyKeywords", "Improved"];
          }

          if (section.name == "keywordRankings") {
            metricNames = jsDig([dashboard, "keyword_rankings"], cc_data).map((ranking) => ranking["keyword"]);
          }

          metricNames.forEach((metricName, metricIndex) => {
            if (!pages[currentPage]) {
              pages[currentPage] = { metricNames: [], pageHeight: 0 };
            }

            if (metricIndex == 0) {
              if (
                ((dashboardOrder[index - 1] && dashboardOrder[index - 1].name.includes("landingPageGroups") && !((index - firstLandingPageIndex) % 2 == 0)) ||
                  (dashboardOrder[index - 1] && dashboardOrder[index - 1].name.includes("keywordGroups") && !((index - firstKeywordIndex) % 2 == 0))) &&
                metricNames.length < 3
              ) {
                pages[currentPage]["pageHeight"] += 0;
              } else {
                console.log("Add 40 pixels for header");
                pages[currentPage]["pageHeight"] += 40;
              }
            }

            if (metricIndex % elementsPerRow == 0 || metricIndex == 0) {
              // 1065px page height - (74px + 10px if header) - 10px bottom padding
              let maxPageHeight = currentPage == 0 ? 971 : 1055;
              if (elementHeight + pages[currentPage]["pageHeight"] > maxPageHeight) {
                console.log(`${elementHeight + pages[currentPage]["pageHeight"]}px Would overflow PDF ${maxPageHeight}px, need to create new page`);
                currentPage += 1;
                if (!pages[currentPage]) {
                  console.log("Initialized new page");
                  pages[currentPage] = { metricNames: [], pageHeight: 40 };
                }
                if (section.name == "keywordRankings") {
                  //for table head
                  pages[currentPage]["pageHeight"] += 25;
                }
              }
              if (metricIndex == 0) {
                if (dashboardOrder[index - 1] && dashboardOrder[index - 1].name.includes("landingPageGroups") && !((index - firstLandingPageIndex) % 2 == 0) && metricNames.length < 3) {
                  pages[currentPage]["pageHeight"] += 0;
                } else {
                  pages[currentPage]["pageHeight"] += 40;
                }
              }

              pages[currentPage]["pageHeight"] += elementHeight;
            }

            pages[currentPage]["metricNames"].push({
              name: metricName,
              section: section.name,
            });

            console.log(
              "section: " +
                section.name +
                " page: " +
                currentPage +
                ", EPR: " +
                elementsPerRow +
                ", name: " +
                metricName +
                ", index: " +
                metricIndex +
                ", page height:" +
                pages[currentPage]["pageHeight"]
            );
          });
        }
      }
    }

    console.log("Pages:");
    console.log(pages);

    if (this.props.enabled) {
      if (jsDig([dashboard, "date"], cc_data)) {
        return (
          <>
            {pages.map((page, index) => {
              return this.generatePdfPage(page, cc_data, dashboard, index);
            })}
          </>
        );
      } else {
        return (
          <>
            <NoData service="seo" />;
          </>
        );
      }
    } else {
      return (
        <>
          <ServiceDisabled service="seo" userIsEmployee={this.props.user_is_employee} />;
        </>
      );
    }
  }
}

export default SEOPdf;
